// @ts-check

import { GRID_PRESETS, kidsGridPresets, isPresetAllowedForResolution } from './grid-presets.js';
import { isEdgePieceId } from './scatter.js';

/**
 * @typedef {'kids' | 'easy' | 'normal' | 'hard' | 'master'} DifficultyMode
 */

/**
 * @typedef {Object} DifficultyChoice
 * @property {DifficultyMode} id
 * @property {string} label
 * @property {string[]} lines - Short notes shown under the button.
 */

/**
 * @typedef {import('./grid-presets.js').GridPreset} GridPreset
 */

/**
 * Highest first, so a mode that does not fit drops to the next one.
 * @type {DifficultyMode[]}
 */
export const DIFFICULTY_ORDER = ['master', 'hard', 'normal', 'easy', 'kids'];

/** Hidden from Create Room and admin pickers; still valid on existing rooms. */
export const HIDDEN_DIFFICULTY_IDS = /** @type {const} */ (['master']);

/** @type {Record<DifficultyMode, number>} */
export const DIFFICULTY_MIN_PIECES = {
  kids: 1,
  easy: 1,
  normal: 100,
  hard: 200,
  master: 400
};

/** Snap distance as a fraction of piece width. Absent on rooms created before modes existed. */
export const DIFFICULTY_SNAP_RATIO = {
  kids: 0.45,
  easy: 0.4,
  normal: 0.3,
  hard: 0.2,
  master: 0.15
};

/**
 * Player-facing names and the short notes under each button.
 * Stored ids are `kids`, `easy`, `normal`, `hard`, and `master`.
 * Create Room and admin pickers omit Master.
 *
 * @type {DifficultyChoice[]}
 */
export const ALL_DIFFICULTY_CHOICES = [
  {
    id: 'kids',
    label: 'Kids',
    lines: [
      'Large varied knobs',
      'Very generous snap distance',
      'Edge pieces in the middle',
      'Snap anywhere',
      'Unlisted room and chat disabled'
    ]
  },
  {
    id: 'easy',
    label: 'Easy',
    lines: [
      'Large varied knobs',
      'Generous snap distance',
      'Edge pieces in the middle',
      'Snap anywhere'
    ]
  },
  {
    id: 'normal',
    label: 'Normal',
    lines: [
      'Classic knobs',
      'Further snap distance',
      'Regular piece scatter',
      'Snap anywhere'
    ]
  },
  {
    id: 'hard',
    label: 'Hard',
    lines: [
      'Small similar knobs',
      'Normal snap distance',
      'Regular piece scatter',
      'Snap anywhere'
    ]
  },
  {
    id: 'master',
    label: 'Master',
    lines: [
      'No knobs',
      'Miniscule snap distance',
      'Pieces scattered far apart',
      'Snap only to edge or solved pieces'
    ]
  }
];

/** Create Room and admin picker entries. Master stays off this list. */
export const DIFFICULTY_CHOICES = ALL_DIFFICULTY_CHOICES.filter((choice) => choice.id !== 'master');

/**
 * Closed SVG path strings (`d` attribute) for 100x100 viewBoxes depicting the
 * characteristic puzzle piece knob silhouettes for each difficulty mode.
 *
 * Generated mathematically using the edge shape profiles:
 * - Kids: Chunky, wide-necked, extra puffy knob (puff 1.35, tab 0.32)
 * - Easy: Large, prominent knob (puff 1.25, tab 0.26)
 * - Normal: Classic balanced knob (puff 1.15, tab 0.20)
 * - Hard: Small, intricate knob (puff 1.05, tab 0.13)
 *
 * @type {Record<DifficultyMode, string>}
 */
export const DIFFICULTY_PIECE_PREVIEWS = {
  kids: 'M21.4,38.6L34.2,38.6C34.2,38 35.5,31.729 37.5,27.929C39,22.829 43.8,22.829 45.2,27.929C47.2,31.729 48.6,38 48.6,38.6L61.4,38.6L61.4,51.4C62,51.4 68.271,52.8 72.071,54.8C77.171,56.2 77.171,61 72.071,62.5C68.271,64.5 62,65.8 61.4,65.8L61.4,78.6L48.6,78.6C48.6,78 47.2,71.729 45.2,67.929C43.8,62.829 39,62.829 37.5,67.929C35.5,71.729 34.2,78 34.2,78.6L21.4,78.6L21.4,65.8C22,65.8 28.271,64.5 32.071,62.5C37.171,61 37.171,56.2 32.071,54.8C28.271,52.8 22,51.4 21.4,51.4L21.4,38.6Z',
  easy: 'M23.5,36.5L37.1,36.5C37.1,36.0 38.3,29.7 40.1,26.6C41.4,23.5 45.6,23.5 46.9,26.6C48.7,29.7 49.9,36.0 49.9,36.5L63.5,36.5L63.5,50.1C64.0,50.1 70.3,51.3 73.4,53.1C76.5,54.4 76.5,58.6 73.4,59.9C70.3,61.7 64.0,62.9 63.5,62.9L63.5,76.5L49.9,76.5C49.9,76.0 48.7,69.7 46.9,66.6C45.6,63.5 41.4,63.5 40.1,66.6C38.3,69.7 37.1,76.0 37.1,76.5L23.5,76.5L23.5,62.9C24.0,62.9 30.3,61.7 33.4,59.9C36.5,58.6 36.5,54.4 33.4,53.1C30.3,51.3 24.0,50.1 23.5,50.1L23.5,36.5Z',
  normal: 'M25.4,34.6L39.8,34.6C39.8,34.2 40.8,29.4 42.4,27.0C43.5,25.4 47.3,25.4 48.4,27.0C50.0,29.4 51.0,34.2 51.0,34.6L65.4,34.6L65.4,49.0C65.8,49.0 70.6,50.0 73.0,51.6C74.6,52.7 74.6,56.5 73.0,57.6C70.6,59.2 65.8,60.2 65.4,60.2L65.4,74.6L51.0,74.6C51.0,74.2 50.0,69.4 48.4,67.0C47.3,65.4 43.5,65.4 42.4,67.0C40.8,69.4 39.8,74.2 39.8,74.6L25.4,74.6L25.4,60.2C25.8,60.2 30.6,59.2 33.0,57.6C34.6,56.5 34.6,52.7 33.0,51.6C30.6,50.0 25.8,49.0 25.4,49.0L25.4,34.6Z',
  hard: 'M27.3,32.7L42.9,32.7C42.9,32.5 43.7,29.4 44.9,27.8C45.8,27.3 48.7,27.3 49.6,27.8C50.8,29.4 51.7,32.5 51.7,32.7L67.3,32.7L67.3,48.3C67.5,48.3 70.7,49.2 72.2,50.4C72.7,51.3 72.7,54.2 72.2,55.1C70.7,56.3 67.5,57.1 67.3,57.1L67.3,72.7L51.7,72.7C51.7,72.5 50.8,69.4 49.6,67.8C48.7,67.3 45.8,67.3 44.9,67.8C43.7,69.4 42.9,72.5 42.9,72.7L27.3,72.7L27.3,57.1C27.5,57.1 30.7,56.3 32.2,55.1C32.7,54.2 32.7,51.3 32.2,50.4C30.7,49.2 27.5,48.3 27.3,48.3L27.3,32.7Z',
  master: 'M28.0,32.0L43.8,32.0C43.8,31.8 44.5,29.2 45.5,27.9C46.3,27.5 48.7,27.5 49.5,27.9C50.5,29.2 51.2,31.8 51.2,32.0L67.0,32.0L67.0,47.8C67.2,47.8 69.8,48.5 71.1,49.5C71.5,50.3 71.5,52.7 71.1,53.5C69.8,54.5 67.2,55.2 67.0,55.2L67.0,71.0L51.2,71.0C51.2,70.8 50.5,68.2 49.5,66.9C48.7,66.5 46.3,66.5 45.5,66.9C44.5,68.2 43.8,70.8 43.8,71.0L28.0,71.0L28.0,55.2C28.2,55.2 30.8,54.5 32.1,53.5C32.5,52.7 32.5,50.3 32.1,49.5C30.8,48.5 28.2,47.8 28.0,47.8L28.0,32.0Z'
};

/**
 * Returns the SVG piece path string (`d` attribute) for the given difficulty mode.
 *
 * @param {unknown} mode
 * @returns {string}
 */
export function difficultyPieceSvgPath(mode) {
  const diff = normalizeDifficulty(mode) || 'normal';
  return DIFFICULTY_PIECE_PREVIEWS[diff] || DIFFICULTY_PIECE_PREVIEWS.normal;
}

/**
 * @param {unknown} value
 * @returns {DifficultyMode|null}
 */
export function normalizeDifficulty(value) {
  if (typeof value !== 'string') return null;
  const lower = value.trim().toLowerCase();
  if (lower === 'kids') return 'kids';
  if (lower === 'easy') return 'easy';
  if (lower === 'normal' || lower === 'medium') return 'normal';
  if (lower === 'hard') return 'hard';
  if (lower === 'master') return 'master';
  return null;
}

/**
 * Mode for a new room or picker. Hidden Master becomes Hard.
 *
 * @param {unknown} value
 * @returns {DifficultyMode}
 */
export function coerceSelectableDifficulty(value) {
  const id = normalizeDifficulty(value) || 'easy';
  if (id === 'master') return 'hard';
  return id;
}

/**
 * Puzzle Size chips for a play mode. Kids shows 5×3, 6×4, and standard sizes up to 84 pieces.
 *
 * @param {unknown} mode
 * @returns {GridPreset[]}
 */
export function gridPresetsForDifficulty(mode) {
  return normalizeDifficulty(mode) === 'kids' ? kidsGridPresets() : GRID_PRESETS;
}

/**
 * Player-facing name for a stored mode. Empty when the room has no mode.
 *
 * @param {unknown} mode
 * @returns {string}
 */
export function difficultyLabel(mode) {
  const id = normalizeDifficulty(mode);
  if (!id) return '';
  const choice = ALL_DIFFICULTY_CHOICES.find((item) => item.id === id);
  return choice ? choice.label : '';
}

/**
 * @param {unknown} mode
 * @returns {number}
 */
export function minPiecesForDifficulty(mode) {
  const id = normalizeDifficulty(mode) || 'easy';
  return DIFFICULTY_MIN_PIECES[id] ?? 1;
}

/**
 * @param {unknown} mode
 * @returns {number|null} Fraction of piece width, or null to keep the legacy pixel threshold.
 */
export function snapRatioForDifficulty(mode) {
  const id = normalizeDifficulty(mode);
  if (!id) return null;
  return DIFFICULTY_SNAP_RATIO[id] ?? null;
}

/**
 * Scatter knobs for a mode. Legacy rooms never call this; they already have positions.
 *
 * @param {unknown} mode
 * @returns {{ placeEdgesInCenter: boolean, marginRatio: number, slotScale: number }}
 */
export function scatterOptionsForDifficulty(mode) {
  const id = normalizeDifficulty(mode) || 'easy';
  if (id === 'master') {
    return { placeEdgesInCenter: false, marginRatio: 0.8, slotScale: 1.8 };
  }
  if (id === 'hard' || id === 'normal') {
    return { placeEdgesInCenter: false, marginRatio: 0.35, slotScale: 1 };
  }
  return { placeEdgesInCenter: true, marginRatio: 0.35, slotScale: 1 };
}

/**
 * Lowers a requested mode when the puzzle does not have enough pieces.
 * A missing mode becomes Easy.
 *
 * @param {unknown} mode
 * @param {number} pieceCount
 * @returns {DifficultyMode}
 */
export function clampDifficulty(mode, pieceCount) {
  const count = Number(pieceCount);
  const pieces = Number.isFinite(count) && count > 0 ? count : 0;
  const requested = normalizeDifficulty(mode) || 'easy';
  const start = DIFFICULTY_ORDER.indexOf(requested);
  for (let i = start < 0 ? DIFFICULTY_ORDER.length - 1 : start; i < DIFFICULTY_ORDER.length; i++) {
    const candidate = DIFFICULTY_ORDER[i];
    if (pieces >= (DIFFICULTY_MIN_PIECES[candidate] ?? 1)) return candidate;
  }
  return 'easy';
}

/**
 * True when the image's allowed presets include one large enough for the mode.
 *
 * @param {unknown} mode
 * @param {GridPreset[]} presets
 * @param {number|null|undefined} longSide
 * @returns {boolean}
 */
export function difficultyAvailable(mode, presets, longSide) {
  const min = minPiecesForDifficulty(mode);
  const pool = normalizeDifficulty(mode) === 'kids' ? kidsGridPresets() : presets;
  return pool.some(
    (preset) => preset.cols * preset.rows >= min && isPresetAllowedForResolution(preset, longSide)
  );
}

/**
 * Highest mode at or below the request that the image can still supply.
 *
 * @param {unknown} mode
 * @param {GridPreset[]} presets
 * @param {number|null|undefined} longSide
 * @returns {DifficultyMode}
 */
export function lowerDifficultyToFit(mode, presets, longSide) {
  const requested = normalizeDifficulty(mode) || 'easy';
  const start = DIFFICULTY_ORDER.indexOf(requested);
  for (let i = start < 0 ? DIFFICULTY_ORDER.length - 1 : start; i < DIFFICULTY_ORDER.length; i++) {
    const candidate = DIFFICULTY_ORDER[i];
    if (difficultyAvailable(candidate, presets, longSide)) return candidate;
  }
  return 'easy';
}

/**
 * Master can lock straight to the board only when the group includes a corner or edge.
 * Other modes, and rooms with no stored mode, can lock any piece.
 *
 * @param {unknown} mode
 * @param {Array<number|string>} pieceIds
 * @param {number} cols
 * @param {number} rows
 * @returns {boolean}
 */
/**
 * Seeded knob profile for one play mode.
 * Rooms with no stored mode keep {@link CLASSIC_SHAPE_PROFILE}.
 *
 * @typedef {Object} PieceShapeProfile
 * @property {number} tabMin
 * @property {number} tabMax
 * @property {number} offsetMax
 * @property {number} puffMin
 * @property {number} puffMax
 * @property {number} neck
 */

/**
 * Kids and Easy share one large, irregular knob profile.
 * Master knobs are tiny and nearly uniform.
 *
 * @type {PieceShapeProfile}
 */
const LARGE_VARIED_SHAPE_PROFILE = {
  tabMin: 0.20,
  tabMax: 0.28,
  offsetMax: 0.15,
  puffMin: 1.22,
  puffMax: 1.30,
  neck: 0.15
};

/**
 * Classic outline used before modes existed, and for Normal.
 * `tabSize * headPuff` stays under ~0.48 so opposite knobs do not meet.
 *
 * @type {PieceShapeProfile}
 */
export const CLASSIC_SHAPE_PROFILE = {
  tabMin: 0.14,
  tabMax: 0.28,
  offsetMax: 0.1,
  puffMin: 1.15,
  puffMax: 1.15,
  neck: 0.13
};

/**
 * @type {Record<DifficultyMode, PieceShapeProfile>}
 */
export const DIFFICULTY_SHAPE_PROFILES = {
  kids: LARGE_VARIED_SHAPE_PROFILE,
  easy: LARGE_VARIED_SHAPE_PROFILE,
  normal: CLASSIC_SHAPE_PROFILE,
  hard: {
    tabMin: 0.12,
    tabMax: 0.20,
    offsetMax: 0,
    puffMin: 1.05,
    puffMax: 1.1,
    neck: 0.1
  },
  master: {
    tabMin: 0,
    tabMax: 0,
    offsetMax: 0,
    puffMin: 1,
    puffMax: 1,
    neck: 0
  }
};

/**
 * @param {String|null|undefined} mode
 * @returns {PieceShapeProfile}
 */
export function shapeProfileForDifficulty(mode) {
  const id = normalizeDifficulty(mode);
  if (!id) return CLASSIC_SHAPE_PROFILE;
  return DIFFICULTY_SHAPE_PROFILES[id] || CLASSIC_SHAPE_PROFILE;
}

/**
 * @param {String|null|undefined} mode
 * @param {any[]} pieceIds
 * @param {any} cols
 * @param {any} rows
 */
export function allowsDirectBoardLock(mode, pieceIds, cols, rows) {
  if (normalizeDifficulty(mode) !== 'master') return true;
  const c = Math.floor(Number(cols));
  const r = Math.floor(Number(rows));
  if (!(c > 0) || !(r > 0)) return true;
  return pieceIds.some((id) => isEdgePieceId(Number(id), c, r));
}
